import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, FlatList, ScrollView, KeyboardAvoidingView } from 'react-native'
import { format } from 'date-fns'
import { Button, Text, Modal, Card, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import SuppliesScreen from '../IncomingSupplies/SuppliesScreen'
import PaymentTransactionsScreen from '../Payments/PaymentTransactionsScreen'
import { updateSupplier, saveNewSupplier, deleteSupplier } from '../../global/Firebase/firebase'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useAtomValue } from 'jotai'
import { productsDictAtom, storesDictAtom } from '../../global/AtomsProvider'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { useTranslation } from 'react-i18next'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import SupplyOrdersScreen from '../SupplyOrders/SupplyOrdersScreen'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
// ATL-968: render the supplier's products in catalog order.
import { getProductsList } from '../../utils/productListHelpers'

export default function SupplierDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail, preselectedStore } = route.params
  const productsDict = useAtomValue(productsDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const postHog = usePostHog()
  const [store, setStore] = useState(preselectedStore || context.user.store?.id)
  const [products, setProducts] = useState(null)
  const [name, setName] = useState(detail?.name) // for editing
  const [editModalVisible, setEditModalVisible] = useState(detail ? false : true)
  const [saveError, setSaveError] = useState('')
  
  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            setEditModalVisible(true)
          }}
          iconName='edit-outline'
          testID='edit-supplier-button'
        />
      ),
    })
    if (detail) {
      identifyEmptyingShelfs()
    }
  }, [store])

  // Log supplier detail for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[SUPPLIER_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [detail])

  const identifyEmptyingShelfs = () => {
    var newProducts = []
    getProductsList(productsDict).forEach(value => {
      if (value.activeInStore[store] && value.supplierID === detail.id) {
        newProducts.push(value)
      }
    })
    setProducts(newProducts)
  }

  const initialRange = () => {
    var today = new Date()
    var d = new Date()
    d.setDate(today.getDate() - 3)
    return {
      startDate: d,
      endDate: today,
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <Text appearance='hint' style={{textAlign: 'center'}}>
          {detail?.id}
        </Text>
        <Text category='h1' style={styles.label}>
          {detail?.name}
        </Text>
          
        { detail && (
          <>
            <Text category='label' style={styles.label}>
              {t('products')}
            </Text>
            <View style={{marginVertical: 10, padding: 20, borderRadius: 25, backgroundColor: theme['background-basic-color-1']}}>
              <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
                setStore(newStore)
              }} />
              { products && products.map((product, index) => (
                <View key={index?.toFixed()} style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderBottomWidth: 1, borderColor: theme['text-basic-color']}}>
                  <Text style={{color: product.storeStock[store] < (product.stockThreshold || 3) ? 'red' : 'black'}}>{product.name}</Text>
                  <Text style={{color: product.storeStock[store] < (product.stockThreshold || 3) ? 'red' : 'black'}}>{product.storeStock[store]?.toFixed(product.type)}</Text>
                </View>
              ))}
            </View>
            <Text category='label' style={styles.label}>
              {t('supplies.payments')}
            </Text>
            <View style={{marginVertical: 10, paddingVertical: 20, borderRadius: 25, backgroundColor: theme['background-basic-color-1']}}>
              <PaymentTransactionsScreen navigation={navigation} supplierID={detail?.id} initialRange={initialRange()} showTotal={true} />
            </View>
            <Text category='label' style={styles.label}>
              {t('supplies.supplies')}
            </Text>
            <View style={{marginVertical: 10, paddingVertical: 20, borderRadius: 25, backgroundColor: theme['background-basic-color-1']}}>
              <SuppliesScreen navigation={navigation} supplierID={detail?.id} initialRange={initialRange()} showTotal={true} />
            </View>
            <Text category='label' style={styles.label}>
              {t('supplyOrders.supplyOrders')}
            </Text>
            <View style={{marginVertical: 10, paddingVertical: 20, borderRadius: 25, backgroundColor: theme['background-basic-color-1']}}>
              <SupplyOrdersScreen 
                navigation={navigation} 
                supplierID={detail?.id} 
                initialRange={initialRange()} 
                showTotal={true} 
              />
            </View>
          </>
        )}
        
        
        <Modal style={styles.modal} visible={editModalVisible} backdropStyle={styles.backdrop} onBackdropPress={() => {
          setEditModalVisible(false)
          setName(detail?.name)
        }}>
          <Card style={styles.modalContainer}>
            <StyledTextInput
              value={name}
              onChangeText={setName}
              style={styles.spacedItem}
            />
            {saveError !== '' && (
              <Text category='p1' status='danger' style={styles.label}>
                {saveError}
              </Text>
            )}
            <Button style={styles.spacedItem} onPress={async () => {
              if (!name || name === '') {
                setSaveError(t('supplies.saveErrorName'))
                return
              }
              if (detail) {
                try {
                  await updateSupplier(detail.ref, {
                    name
                  })
                  sendFBAnalyticsEvent('update_supplier')
                  postHog.capture('supplier_updated')
                  setName(detail.name)
                } catch(error) {
                  Sentry.captureException(error)
                }
              } else {
                try {
                  await saveNewSupplier(context.db, {
                    name,
                    active: true,
                    organizationId: context.user.organizationId,
                    lastChangeBy: context.user.uid,
                    lastChangeAt: new Date(),
                    dailyStatID: getDailyStatID(null, new Date()),
                    lastChangeStore: context.user.store.id,
                  })
                  sendFBAnalyticsEvent('new_supplier')
                  postHog.capture('supplier_created')
                } catch(error) {
                  Sentry.captureException(error)
                }
              }
              setEditModalVisible(false)
              navigation.goBack()
            }}>
              {t('save')}
            </Button>
            { detail != null && (
              <Button status='danger' style={styles.spacedItem} onPress={() => {
                setEditModalVisible(false)
                let supplierProducts = []
                Object.values(productsDict).forEach(value => {
                  if (value.supplierID === detail.id) {
                    supplierProducts.push(value)
                  }
                })
                try {
                  deleteSupplier(detail.ref, supplierProducts, storesDict).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
                } catch(error) {
                  Sentry.captureException(error)
                }
                navigation.goBack()
              }}>
                {t('supplies.deleteSupplier')}
              </Button>
            )}
            <Button status='basic' style={styles.spacedItem} onPress={() => {
              setEditModalVisible(false)
              setName(detail?.name)
              if (detail == null) {
                navigation.goBack()
              }
            }}>
              {t('cancel')}
            </Button>
          </Card>
        </Modal>
          
        <View style={{height: 100}}></View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  modal: {
    top: 100,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 300,
    height: '80%',
    padding: 16,
    margin: 32
  },
  spacedItem: {
    marginVertical: 4,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
});